import React, { useMemo } from 'react';

// Koordinater i arenaen (300 x 71): avatarens hånd og bossens centrum.
const AX = 44;
const AY = 34;
const BX = 258;
const BY = 38;
const FLIGHT = { '--bx': `${BX - AX}px` };
const fill = { position: 'absolute', pointerEvents: 'none' };
const visible = (c) => (c && c !== 'transparent' ? c : null);

// Ét projektil: ydre div flyver (animation), indre div former det.
function Shot({ spell, size, color, delay = 0, anim, children, top = AY, opacity = 1 }) {
  return (
    <div style={{ ...fill, left: AX - size / 2, top: top - size / 2, width: size, height: size, zIndex: 5, opacity, ...FLIGHT, animation: `${anim} ${spell.dur}ms ease-in ${delay}ms both` }}>
      {children || <div style={{ width: size, height: size, borderRadius: '50%', background: color, boxShadow: visible(spell.glow) ? `0 0 8px ${spell.glow}` : undefined }} />}
    </div>
  );
}

function Bolt({ spell, anim }) {
  const { shape, color, trail, size = 10, count = 1 } = spell;
  if (shape === 'arrow') {
    return Array.from({ length: count }, (_, i) => (
      <div key={i} style={{ ...fill, left: AX, top: AY + (i - (count - 1) / 2) * 7 - 1, width: 14, height: 2, background: color, zIndex: 5, ...FLIGHT, animation: `${anim} ${spell.dur}ms ease-in ${i * 70}ms both` }} />
    ));
  }
  const body = (s) => {
    if (shape === 'diamond') return <div style={{ width: s, height: s, background: color, transform: 'rotate(45deg)', boxShadow: `0 0 8px ${spell.glow}` }} />;
    if (shape === 'axe') return <div style={{ width: s, height: s, border: `3px solid ${color}`, borderRadius: '50% 0 50% 0', boxSizing: 'border-box', animation: 'zrmSpin .35s linear infinite' }} />;
    return undefined;
  };
  return (
    <>
      {[3, 2, 1].map((i) => (
        <Shot key={i} spell={spell} anim={anim} size={Math.max(3, size * (1 - i * 0.22))} color={trail || color} delay={i * 45} opacity={0.6 - i * 0.1}>
          {body(Math.max(3, size * (1 - i * 0.22)))}
        </Shot>
      ))}
      <Shot spell={spell} anim={anim} size={size} color={color}>{body(size)}</Shot>
    </>
  );
}

function Swarm({ spell }) {
  const dots = useMemo(() => Array.from({ length: 14 }, () => ({
    dx: -(60 + Math.random() * 150), dy: (Math.random() * 2 - 1) * 30, delay: Math.random() * 150,
  })), []);
  return dots.map((d, i) => (
    <div key={i} style={{ ...fill, left: BX - 2, top: BY - 2, width: 4, height: 4, background: spell.color, zIndex: 5, '--dx': `${d.dx}px`, '--dy': `${d.dy}px`, animation: `zrmConverge ${spell.dur}ms ease-in ${d.delay}ms both` }} />
  ));
}

// Selve spell'en (projektil, stråle, lyn ...). Nærkamp har intet projektil: avataren løber selv.
export default function SpellFx({ spell }) {
  const { kind, shape, color, glow, size = 10, dur } = spell;
  switch (kind) {
    case 'bolt': return <Bolt spell={spell} anim="zrmBolt" />;
    case 'arc': return <Bolt spell={spell} anim="zrmArc" />;
    case 'beam':
      return <div style={{ ...fill, left: AX + 2, top: AY - 3, width: BX - AX - 2, height: 6, background: color, boxShadow: `0 0 8px ${glow}`, transformOrigin: '0 50%', zIndex: 5, animation: `zrmBeam ${dur + 250}ms both` }} />;
    case 'lightning':
      return (
        <svg width="300" height="71" style={{ ...fill, left: 0, top: 0, zIndex: 5, filter: `drop-shadow(0 0 4px ${color})`, animation: `zrmFlicker ${dur + 150}ms steps(1) both` }}>
          <polyline points="46,38 90,20 130,46 170,24 210,48 254,38" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    case 'drop':
      return shape === 'pillar'
        ? <div style={{ ...fill, left: BX - 5, top: -12, width: 10, height: BY + 12, background: color, boxShadow: `0 0 10px ${glow}`, transformOrigin: '50% 0', zIndex: 5, animation: `zrmPillar ${dur + 150}ms both` }} />
        : <div style={{ ...fill, left: BX - size / 2, top: BY - size / 2, width: size, height: size, borderRadius: '50%', background: color, boxShadow: `0 0 10px ${glow}`, zIndex: 5, animation: `zrmDrop ${dur}ms ease-in both` }} />;
    case 'nova':
      return [0, 120].map((delay) => (
        <div key={delay} style={{ ...fill, left: BX - 20, top: BY - 20, width: 40, height: 40, borderRadius: '50%', border: `3px solid ${color}`, boxSizing: 'border-box', zIndex: 5, animation: `zrmRingOut ${dur + 150}ms ease-out ${delay}ms both` }} />
      ));
    case 'swarm': return <Swarm spell={spell} />;
    default: return null;
  }
}
